import { SelectionModel } from '@angular/cdk/collections';
import { AsyncPipe, CommonModule, JsonPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { RouterModule } from '@angular/router';
import { FeatureFlag, NewPermission } from '@core/enums';
import { FieldDef, User, UserAssociation } from '@core/models';
import { AccountService } from '@core/services';
import { Actions, ofActionSuccessful } from '@ngxs/store';
import { TableTasksProjectRendererComponent } from '@shared/components/ts-table-renderers/table-tasks-project-renderer/table-tasks-project-renderer.component';
import { TableTasksPropertyRendererComponent } from '@shared/components/ts-table-renderers/table-tasks-property-renderer/table-tasks-property-renderer.component';
import { TableTasksSectionRendererComponent } from '@shared/components/ts-table-renderers/table-tasks-section-renderer/table-tasks-section-renderer.component';
import { TableTasksStatusRendererComponent } from '@shared/components/ts-table-renderers/table-tasks-status-renderer/table-tasks-status-renderer.component';
import { defaultTaskTableFields } from '@shared/modules/legacy-tasks/list/list.constants';
import { LegacyTaskTableModule } from '@shared/modules/legacy-tasks/list/table/table.module';
import { AccountState } from '@state/account/state';
import { UserState } from '@state/user/state';
import { TaskGroupService } from '@tasks/services/task-group.service';
import { TaskTitleCellComponent } from '@tasks/task-view-list/task-title-cell.component';
import { TasksService } from '@tasks/services/tasks.service';
import { TaskActions } from '@tasks/state/actions';
import { TaskState } from '@tasks/state/state';
import { TaskViewBase } from '@tasks/task-view-base';
import { Task, TaskSort, TaskView } from '@tasks/tasks.types';
import { TaskUtils } from '@tasks/tasks.utils';
import {
  ColumnResizedEvent,
  GetRowIdFunc,
  GridApi,
  IsFullWidthRowParams,
  IsGroupOpenByDefaultParams,
  SortChangedEvent,
} from 'ag-grid-enterprise';
import cloneDeep from 'lodash-es/cloneDeep';
import moment from 'moment';
import { debounceTime, filter, Observable, Subject, tap } from 'rxjs';
import { TsTableService } from 'tsui';
import { TsTableGroupedService } from 'tsui/lib/services/ts-table-grouped.service';
import { TsTableGroupedComponent } from 'tsui/lib/ts-table-grouped';
import { ConfirmationDialogService } from '@shared/components/widgets/dialog/confirmation/confirmation-dialog.service';
import { TaskPredecessorService } from '@tasks/services/task-predecessor.service';
import { TableTasksLatestCommentRendererComponent } from '@shared/components/ts-table-renderers/table-tasks-latest-comment-renderer/table-tasks-latest-comment-renderer.component';

const AG_GRID_AUTOGROUP_COLUMN_ID = 'ag-Grid-AutoColumn';
import { TASK_WORKFLOW_STATUSES } from '@tasks/services/task-status.service';

@Component({
  selector: 'app-task-view-list',
  standalone: true,
  imports: [
    CommonModule,
    AsyncPipe,
    JsonPipe,
    RouterModule,
    MatButtonModule,
    LegacyTaskTableModule,
    TsTableGroupedComponent,
  ],
  host: {
    class: 'task-view-list',
    '[class.header-aggregated-tasks]': 'isAggregatedTask',
    '[class.header-global]': 'isGlobalTask',
  },
  templateUrl: './task-view-list.component.html',
  styleUrls: ['./task-view-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskViewListComponent extends TaskViewBase implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private taskGroupService = inject(TaskGroupService);
  private tasksService = inject(TasksService);
  private confirmationService = inject(ConfirmationDialogService);
  private taskPredecessorService = inject(TaskPredecessorService);

  groupByField: string = '';
  sortField: TaskSort[];
  columnDefs = [];
  rowData: any[] = [];
  autoGroupColumnDef;
  selection = new SelectionModel<Task>(true, [], false, (t1, t2) => t1.id === t2.id);
  previousSelection: any[] = [];

  public users: User[];

  @Output() newTask = new EventEmitter<Partial<Task>>();
  @Output() columnsMoved = new EventEmitter<string[]>();
  @Output() onSortChange = new EventEmitter<TaskSort[]>();
  @Output() columnsResized = new EventEmitter<any>();

  tasks;
  taskList;

  isSectionOrGroup = (t) => t.task_type === 'section' || t.group;

  get hasEditTaskPermission(): boolean {
    const isNewPermissionEnabled = this.store.selectSnapshot(
      AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS),
    );

    if (!isNewPermissionEnabled) {
      return true;
    }

    if (this.isGlobalTask) {
      return this.store.selectSnapshot(UserState.hasPermission(NewPermission.GLOBAL_TASK_LISTS_DELETE));
    } else {
      return this.store.selectSnapshot(UserState.hasPermission(NewPermission.AGGREGATED_TASK_LISTS_DELETE));
    }
  }

  taskView$: Observable<TaskView> = this.store.select(TaskState.getTaskView).pipe(filter((taskList) => !!taskList));

  // TODO MJR -> NECESSARY TO TEMP USAGE OF THE OLD TABLE
  selectedFields: FieldDef[] = [];

  // TODO MJR -> NECESSARY TO TEMP USAGE OF THE OLD TABLE
  routeData;

  gridOptions = {
    animateRows: true,
    suppressAggAtRootLevel: true,
    suppressScrollOnNewData: true,
    enableGroupEdit: true,
    defaultColDef: {
      editable: this.hasEditTaskPermission,
    },
    cellStyle: {
      height: '100%',
      display: 'flex ',
      'justify-content': 'center',
      'align-items': 'center ',
    },
    getRowHeight: (params) => {
      if (params.data && params.data?.task_type === 'key_date') {
        return 55;
      }
      return 42;
    },
    getRowClass: (params) => {
      if (params.data && params.data?.task_type === 'section') {
        return 'section-row';
      }
      if (params.data && params.data?.task_type === 'key_date') {
        return 'key-date-row';
      }
      return '';
    },
    isGroupOpenByDefault: (params: IsGroupOpenByDefaultParams) => {
      return params.rowNode.data?.expanded;
    },
    isFullWidthRow: (params: IsFullWidthRowParams) => {
      return params.rowNode.data?.task_type === 'section';
    },
    fullWidthCellRenderer: TableTasksSectionRendererComponent,
    cellRendererParams: {},
    context: {
      openTaskDetail: (task: Task, showComments = false) => {
        this.openTaskDetail(task, showComments);
      },
      onNewTask: (newTask) => {
        this.createTask(newTask);
      },
      onLinkKeyDate: (newLinkedKeyDate: Task) => {
        this.linkKeyDate(newLinkedKeyDate);
      },
      openCloseSection: (task: Task) => {
        task.expanded = !task.expanded;
        this.store.dispatch(new TaskActions.UpdateTask(task));
      },
    },
  };
  gridApi: GridApi;

  private columnResizeSubject = new Subject<ColumnResizedEvent>();

  constructor(
    private readonly tsTableGroupedService: TsTableGroupedService,
    private readonly accountService: AccountService,
    private actions$: Actions,
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.actions$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofActionSuccessful(TaskActions.BulkUpdateSelectedTasks),
        tap((res) => {
          const selectedTasks = this.store.selectSnapshot(TaskState.getSelectedTasks);
          for (const selectedTask of selectedTasks) {
            const newTaskFromState = this.store.selectSnapshot(TaskState.getTaskById(+selectedTask.id));
            this.gridApi.forEachNode((node) => {
              if (+node.data?.id === +newTaskFromState?.id) {
                node.updateData({ ...node.data, ...newTaskFromState });
                this.cdr.detectChanges();
              }
            });
          }
        }),
      )
      .subscribe((res) => {
        // only update the rows if the groupByField is updated
        if (Object.keys(res.newTaskParams).includes(this.groupByField)) {
          this.updateRowData();
        }
      });

    this.actions$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofActionSuccessful(TaskActions.UpdateTask),
        tap((res) => {
          const newTaskFromState = this.store.selectSnapshot(TaskState.getTaskById(res.task.id));
          this.gridApi.forEachNode((node) => {
            if (+node.data?.id === +newTaskFromState?.id) {
              node.updateData({ ...node.data, ...newTaskFromState });
            }
          });
        }),
        debounceTime(2000),
        tap(() => {
          if (this.groupByField !== '') {
            this.gridApi.setGridOption('loading', true);
          }
        }),
        debounceTime(300),
      )
      .subscribe(() => {
        // if groupping is enabled, update the rows
        if (this.groupByField !== '') {
          this.updateRowData();
        }

        this.gridApi.setGridOption('loading', false);
      });

    this.actions$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofActionSuccessful(
          TaskActions.GetTasks,
          TaskActions.CreateSubTask,
          TaskActions.CreateTask,
          TaskActions.DeleteTask,
          TaskActions.FilterTasks,
          TaskActions.BulkDeleteSelectedTasks,
          TaskActions.BulkDuplicateSelectedTasks,
          TaskActions.ReorderTasks,
          TaskActions.UpdateCurrentTaskNums,
        ),
      )
      .subscribe(() => {
        this.updateRowData();
      });

    this.columnResizeSubject.pipe(debounceTime(1000)).subscribe((event) => {
      const columnState = this.gridApi.getColumnState();
      this.columnsResized.emit(columnState);
    });

    this.accountService.userAssociations$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((userAssociations: UserAssociation[]) => {
        this.users = userAssociations.map((userAssociation) => userAssociation.user);
      });

    this.activatedRoute.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.routeData = {
        ...data,
        taskFieldDefs: this.selectedFields,
        users: this.users,
        options: { ...data.options },
      };
    });

    this.taskView$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((taskView) => {
      this.taskList = taskView;

      if (!this.taskList?.definition?.fields || this.taskList?.definition?.fields?.length === 0) {
        this.taskList.definition.fields = defaultTaskTableFields.map((field) => field.name);
      }

      if (this.groupByField !== this.taskList?.definition?.table?.groupBy) {
        this.groupByField = this.taskList?.definition?.table?.groupBy || '';
      }

      this.laodTableColumns();
    });
  }

  linkKeyDate(newLinkedKeyDate: Task) {
    this.store.dispatch(new TaskActions.UpdateTask(newLinkedKeyDate));
  }

  onColumnResized(event: any) {
    if (event.source === 'uiColumnResized') {
      this.columnResizeSubject.next(event);
    }
  }

  onGridReady(event: any) {
    this.gridApi = event.api as GridApi;
    this.updateRowData();
  }

  onRowDataUpdated(event: any) {
    // If new data is loaded and the column widthds are not set, we need to auto size the columns
    // to fit the new data
    let customColumnState = this.taskList?.definition?.table?.columnsState;
    let autoSizeColumn = !customColumnState?.length;
    if (autoSizeColumn) {
      this.gridApi?.autoSizeAllColumns();
    }
  }

  setTableSort() {
    if (!this.gridApi) {
      return;
    }

    this.sortField = this.taskList?.definition?.table?.sort;
    let customColumnState = this.taskList?.definition?.table?.columnsState;

    if (customColumnState?.length && this.sortField?.length) {
      customColumnState = customColumnState.map((col) => {
        if (col.colId === this.sortField?.[0]?.field) {
          col.sort = this.sortField?.[0]?.order;
        } else if (col.colId === AG_GRID_AUTOGROUP_COLUMN_ID && this.sortField?.[0]?.field === 'title') {
          col.sort = this.sortField?.[0]?.order;
        } else {
          col.sort = undefined;
        }

        return col;
      });
    } else if (this.sortField?.length && !customColumnState?.length) {
      customColumnState = [
        {
          colId: this.sortField?.[0]?.field === 'title' ? 'ag-Grid-AutoColumn' : this.sortField?.[0]?.field,
          sort: this.sortField?.[0]?.order,
        },
      ];
    } else if (!this.sortField?.length && customColumnState?.length) {
      customColumnState = customColumnState.map((col) => {
        col.sort = null;
        return col;
      });
    }

    this.gridApi.applyColumnState({
      state: customColumnState || [],
      defaultState: { sort: null },
    });
  }

  updateRowData() {
    const tasks = this.transformDataToAgGridFormat(cloneDeep(this.store.selectSnapshot(TaskState.getTasks)));
    this.tasks = tasks;
    this.rowData = tasks;
    this.setTableSort();
    this.gridApi.setGridOption('loading', false);
    this.cdr.detectChanges();
  }

  onStartEditingCell(event: any) {
    const task = event.data;

    if (event.colDef.field === 'start_date' || event.colDef.field === 'due_date') {
      this.taskPredecessorService.isPredecessorAlertModalNeeded(task, event.colDef.field).subscribe((response) => {
        if (response.modalSkipped) {
          return;
        }

        if (response.removePredecessors) {
          this.store.dispatch(new TaskActions.UpdateTask({ ...task, predecessors: [] }, null, false));

          const hasLinkedTo = task.linked_to && task.linked_to.length > 0;
          if (hasLinkedTo) {
            this.store.dispatch(new TaskActions.UnlinkTask(task.linked_to[0]));
          }
        } else {
          this.gridApi.stopEditing();
        }
      });
    }
  }

  onCellEditingStopped(event: any) {
    this.store.dispatch(new TaskActions.UpdateTask(event.data));
  }

  laodTableColumns(reloadRows = false) {
    this.selectedFields = this.taskList?.definition?.fields?.reduce((acc: FieldDef[], field: string) => {
      const selectedField = this.lookupField(field);
      if (selectedField) {
        acc.push(selectedField);
      }
      return acc;
    }, [] as FieldDef[]);

    this.columnDefs = this.selectedFields.filter((field) => field.name !== 'title');
    this.autoGroupColumnDef = {
      headerName: 'Title',
      field: 'title',
      suppressHeaderMenuButton: true,
      editable: false,
      lockPinned: true,
      lockPosition: 'left',
      wrapText: true,
      autoHeight: true,
      comparator: () => 0,
      rowDrag: this.hasEditTaskPermission && !this.isAggregatedTask && !this.groupByField && !this.sortField?.length,
      minWidth: 100,
      width: this.getColumnWidth(AG_GRID_AUTOGROUP_COLUMN_ID),
      cellRenderer: TaskTitleCellComponent,

      onCellClicked: (params) => {
        this.openTaskDetail(params.data);
      },
      context: {
        openCloseSection: (task: Task) => {
          task.expanded = !task.expanded;
        },
      },
    };
    this.setColumnsAttributes();
    this.cdr.detectChanges();
  }

  public getRowId: GetRowIdFunc = (params) => {
    return String(`${params.data.id}`);
  };

  getColumnWidth(colId: string): number {
    const columnState = this.taskList?.definition?.table?.columnsState;
    const column = columnState?.find((col) => col.colId === colId);
    if (column) {
      return column.width;
    }
  }

  onRowDragEnd(event: any) {
    const movingNode = event.node;
    const overNode = event.overNode;
    const rowNeedsToMove = movingNode !== overNode;
    if (!overNode) {
      return;
    }

    if (rowNeedsToMove) {
      // the list of rows we have is data, not row nodes, so extract the data
      const movingData = movingNode.data;
      const overData = overNode!.data;
      const fromIndex = this.tasks.findIndex((task) => +task.id === +movingData.id);
      const toIndex = this.tasks.findIndex((task) => +task.id === +overData.id);
      this.tasks = TaskUtils.deepUpdateTaskById(this.tasks, { ...movingData });
      const newStore = cloneDeep(this.tasks);
      moveInArray(newStore, fromIndex, toIndex);
      this.tasks = newStore.filter((task) => task).map((task, index) => ({ ...task, rank: index }));
      this.store.dispatch(new TaskActions.ReorderTasks(this.taskList.id, this.tasks));
      this.gridApi.setGridOption('rowData', this.tasks);
    }
    function moveInArray(arr: any[], fromIndex: number, toIndex: number) {
      const element = arr[fromIndex];
      arr.splice(fromIndex, 1);
      arr.splice(toIndex, 0, element);
    }
  }

  onColumnMoved(event: any): void {
    let stateAgGrid = this.gridApi.getColumnState();
    let columnFields = stateAgGrid
      .map((column) => (column.colId === AG_GRID_AUTOGROUP_COLUMN_ID ? 'title' : column.colId))
      .filter((column) => column !== 'checkbox');
    this.columnsMoved.emit(columnFields);
  }

  onSortChanged(event: SortChangedEvent): void {
    const sortedColumn = event.columns.filter((col) => col.getSort());
    if (
      this.sortField?.[0]?.field === sortedColumn[0]?.getColDef().field &&
      this.sortField?.[0]?.order === sortedColumn[0]?.getSort()
    ) {
      return;
    }

    this.sortField = [
      {
        field: sortedColumn[0]?.getColDef().field,
        order: sortedColumn[0]?.getSort(),
      },
    ];
    this.sortField = this.sortField[0].field ? this.sortField : undefined;

    if (event.source !== 'api') {
      this.onSortChange.emit(this.sortField);
    }
  }

  setColumnsAttributes() {
    this.columnDefs.map((fieldDef) => {
      const editable =
        this.hasEditTaskPermission &&
        fieldDef.name !== 'pipeline_id' &&
        !fieldDef.meta.readonly &&
        !fieldDef.name.startsWith('project_') &&
        !fieldDef.name.startsWith('property_');
      fieldDef.tsTableOptions = {
        ...fieldDef.tsTableOptions,
        editable,
      };

      const nonEditableStyle = () => {
        if (!fieldDef.tsTableOptions.editable) {
          return { background: '#fbfbfb', 'work-wrap': 'break-word' };
        }

        return fieldDef.tsTableOptions.cellStyle;
      };

      if (!editable) {
        fieldDef.tsTableOptions.cellStyle = nonEditableStyle;
      }

      const isDateField = ['start_date', 'due_date', 'end_date'].includes(fieldDef.name);
      const isDueDateField = ['due_date', 'end_date'].includes(fieldDef.name);

      if (isDateField) {
        fieldDef.readonly = false;
        fieldDef.meta.readonly = false;

        const baseTableOptions = {
          editable: true,
          cellStyle: (params) => {
            if (params.data.task_type === 'key_date' && params.data.linked_key_date_field_as_target) {
              return { background: '#fbfbfb', 'work-wrap': 'break-word', pointerEvents: 'none' };
            }

            if (isDueDateField && params.data.workflow_state !== 'complete' && this.isOverdue(params.value)) {
              return { color: 'red' };
            }

            return null;
          },
          onCellClicked: (params) => {
            if (params.data.task_type === 'key_date' && params.data.linked_key_date_field_as_target) {
              params.api.stopEditing();
            }
          },
        };

        fieldDef.tsTableOptions = {
          ...fieldDef.tsTableOptions,
          ...baseTableOptions,
        };
      }

      if (fieldDef.name === 'workflow_state') {
        fieldDef.meta.options = TASK_WORKFLOW_STATUSES.map((status) => ({ id: status.key, name: status.label }));
        fieldDef.tsTableOptions = {
          editable: true,
          cellRenderer: TableTasksStatusRendererComponent,
        };
      }

      if (fieldDef.name === 'project_title') {
        fieldDef.tsTableOptions = {
          editable: false,
          cellRenderer: TableTasksProjectRendererComponent,
          cellStyle: nonEditableStyle,
        };
      }

      if (fieldDef.name === 'property_name') {
        fieldDef.tsTableOptions = {
          editable: false,
          cellRenderer: TableTasksPropertyRendererComponent,
          cellStyle: nonEditableStyle,
        };
      }

      if (fieldDef.name === 'project_stage_id') {
        fieldDef.tsTableOptions = {
          editable: false,
          cellRenderer: (params) => params.data.project_stage,
          cellStyle: nonEditableStyle,
        };
      }

      if (fieldDef.name === 'project_phase_id') {
        fieldDef.tsTableOptions = {
          editable: false,
          cellRenderer: (params) => params.data.project_phase,
          cellStyle: nonEditableStyle,
        };
      }

      if (fieldDef.name === 'last_comment_str') {
        fieldDef.tsTableOptions = {
          ...fieldDef.tsTableOptions,
          editable: false,
          autoHeight: true,
          wrapText: true,
          width: 400,
          cellRenderer: TableTasksLatestCommentRendererComponent,
          minWidth: 20,
          cellStyle: nonEditableStyle,
        };
      }

      if (fieldDef.name === 'following_user_ids') {
        fieldDef.tsTableOptions = {
          ...fieldDef.tsTableOptions,
          cellRenderer: (params) => {
            if (!params?.value) {
              return '';
            }

            if (Array.isArray(params.value)) {
              const users = this.users.filter((user) => params.value.includes(user.id));
              return users.map((user) => user.name).join(', ');
            }

            const user = this.users.find((user) => +user.id === +params.value);
            return user?.name || '';
          },
        };
      }

      fieldDef.tsTableOptions['width'] = this.getColumnWidth(fieldDef.name);
    });
  }

  isAllSelected(event) {
    const numSelected = event.length;
    const numRows = this.tasks?.filter((t) => t.task_type !== 'section' && !t.group).length;
    return numSelected === numRows;
  }

  masterToggle(): void {
    for (const t of this.tasks) {
      if (t.task_type !== 'section' && !t.group) {
        this.selection.select(t);
      }
    }

    this.store.dispatch(new TaskActions.SetSelectedTasks(this.selection.selected));
  }

  toggleTaskSelection(task: Task): void {
    this.selection.toggle(task);
    this.store.dispatch(new TaskActions.SetSelectedTasks(this.selection.selected));
  }

  onSelectionChanged(event) {
    event = event.filter((t) => t.task_type !== 'section' && !t.group);
    // remove all
    if (!event.length) {
      this.store.dispatch(new TaskActions.SetSelectedTasks([]));
      this.previousSelection = [];
      return;
    }

    // select all
    if (this.isAllSelected(event)) {
      this.masterToggle();
      return;
    }

    // remove olders
    this.selection.selected.forEach((task) => {
      if (!event.find((t) => t.id === task.id)) {
        this.selection.deselect(task);
      }
    });

    // add news
    event.forEach((task) => {
      if (!this.selection.selected.find((t) => t.id === task.id)) {
        this.selection.select(task);
      }
    });

    this.store.dispatch(new TaskActions.SetSelectedTasks(this.selection.selected));
  }

  getDataPath(data: any) {
    return data?.path;
  }

  lookupField(name: string): FieldDef | undefined {
    return this.taskFields.find((fieldDef) => fieldDef.name === name);
  }

  private isOverdue(dueDate): boolean {
    return moment(dueDate, moment.ISO_8601).isBefore(moment(), 'days');
  }

  private transformDataToAgGridFormat(tasks: Task[]): Task[] {
    return this.taskGroupService.transformDataToAgGridTreeData(tasks);
  }
}
